import * as Yup from "yup";

const mobileRule = () =>
  Yup.string().matches(/^[0-9]{10,15}$/, "Mobile phone number must be 10-15 digits");

// Per-step schema for the "New client" wizard. Keyed by step name (Identity/
// Contact/Address/Banking/Linked/Review) rather than a numeric tab index, so
// the wizard's step order can change without renumbering this file.
export const getStepValidationSchema = (step, isNaturalClient, clients) => {
  switch (step) {
    case "Identity":
      return isNaturalClient
        ? Yup.object({
            name: Yup.string().required("Please enter first name"),
            surname: Yup.string().required("Please enter last name"),
            social_security_number: Yup.string()
              .nullable()
              .test(
                "unique-ssn",
                "This Social Security already exists",
                function (value) {
                  if (!value) return true;
                  if (!clients) return true;
                  const ssnList = clients.map((client) => client.social_security_number);
                  return !ssnList.includes(value);
                }
              ),
          })
        : Yup.object({
            legal_company_name: Yup.string().required("Please enter legal company name"),
            legal_fiscal_code: Yup.string()
              .matches(
                /^[A-Z0-9]{6,12}$/,
                "Fiscal code must be alphanumeric and 6-12 characters"
              )
              .required("Please enter legal fiscal code"),
            legal_unique_registration_code: Yup.string()
              .nullable()
              // "Same as fiscal code" mirrors legal_fiscal_code without
              // writing it back into this field (see fieldConfig.js's
              // getEffectiveValue) — so validate whichever value will
              // actually be submitted, via the sibling toggle/fiscal-code
              // fields on this.parent, rather than this field's own value.
              .test(
                "unique-urc",
                "This unique registration code already exists",
                function (value) {
                  const effective = this.parent.legal_urc_same_as_fiscal
                    ? this.parent.legal_fiscal_code
                    : value;
                  if (!effective) return true;
                  if (!clients) return true;
                  const urcList = clients.map((client) => client.legal_unique_registration_code);
                  return !urcList.includes(effective);
                }
              )
              .test(
                "urc-format",
                "Must be alphanumeric 6-12 chars",
                function (value) {
                  const effective = this.parent.legal_urc_same_as_fiscal
                    ? this.parent.legal_fiscal_code
                    : value;
                  if (!effective) return true;
                  return /^[A-Z0-9]{6,12}$/.test(effective);
                }
              ),
            legal_trade_register_number: Yup.string()
              .matches(
                /^.{7,20}$/,
                "Trade register number must be 7-20 characters long (any characters allowed)"
              )
              .required("Please enter a trade register number"),
          });

    case "Contact":
      return Yup.object({
        personal_email: isNaturalClient
          ? Yup.string()
              .email("Please enter a valid personal email")
              .required("Please enter personal email")
          : Yup.string().email("Please enter a valid personal email").notRequired(),
        business_email: !isNaturalClient
          ? Yup.string()
              .email("Please enter a valid business email")
              .required("Please enter business email")
          : Yup.string().notRequired(),
        mobile_phone_number: mobileRule().required("Please enter mobile phone number"),
      });

    case "Address":
      return Yup.object({
        country: Yup.string().required("Please enter country"),
        region: Yup.string().required("Please enter region"),
        city: Yup.string().required("Please enter city"),
        postal_code: isNaturalClient
          ? Yup.string().notRequired()
          : Yup.string().notRequired(),
        address: Yup.string().required("Please enter address"),
      });

    case "Banking":
      // No cross-check today (either IBAN, both, or neither is accepted).
      return Yup.object({});

    case "Linked":
      // Only meaningful for NATURAL clients — see ClientModal's FLOW.
      return Yup.object({
        additional_contact2_name: Yup.string().when("add_second_client", {
          is: true,
          then: () => Yup.string().required("Please enter second client's first name"),
          otherwise: () => Yup.string().notRequired(),
        }),
        additional_contact2_surname: Yup.string().when("add_second_client", {
          is: true,
          then: () => Yup.string().required("Please enter second client's last name"),
          otherwise: () => Yup.string().notRequired(),
        }),
        additional_contact2_ssn: Yup.string().when("add_second_client", {
          is: true,
          then: () =>
            Yup.string()
              .nullable()
              .test(
                "unique-ssn-second",
                "This Social Security already exists",
                function (value) {
                  if (!value) return true;
                  if (!clients) return true;
                  const ssnList = clients.map((client) => client.social_security_number);
                  return !ssnList.includes(value);
                }
              )
              .test(
                "different-from-first-ssn",
                "Second client's SSN cannot be the same as the first client's SSN",
                function (value) {
                  if (!value) return true;
                  const firstSSN = this.parent.social_security_number;
                  return value !== firstSSN;
                }
              ),
          otherwise: () => Yup.string().notRequired(),
        }),
        additional_contact2_mobile: Yup.string().when("add_second_client", {
          is: true,
          then: () => mobileRule().required("Please enter second client's mobile number"),
          otherwise: () => Yup.string().notRequired(),
        }),
        additional_contact2_email: Yup.string().when("add_second_client", {
          is: true,
          then: () =>
            Yup.string()
              .email("Please enter a valid email")
              .required("Please enter second client's email"),
          otherwise: () => Yup.string().notRequired(),
        }),
      });

    default:
      // "Review" has no fields of its own.
      return Yup.object({});
  }
};

// Standalone issue count for a step, independent of Formik's currently active
// schema/touched state — used to drive the step-progress strip and the
// Review step's per-section status, which need every step's validity at
// once (Formik itself only validates the one schema it's currently wired to).
export const countStepIssues = (step, isNaturalClient, clients, values) => {
  if (step === "Linked" && !values.add_second_client) return 0;
  const schema = getStepValidationSchema(step, isNaturalClient, clients);
  try {
    schema.validateSync(values, { abortEarly: false });
    return 0;
  } catch (err) {
    return err.inner && err.inner.length ? err.inner.length : 1;
  }
};
